import type { SignatureDocument, SignatureRequest } from '@dnx/contracts';
import { Alert, Badge, Button, Dialog, Field, Input, Spinner } from '@dnx/ui';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Printer, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

export const patientSignaturesQuery = (patientId: string) => queryOptions({
  queryKey: ['signatures', 'patient', patientId], queryFn: () => api<SignatureRequest[]>(`/patients/${patientId}/signatures`),
});
export const quoteSignaturesQuery = (quoteId: string) => queryOptions({
  queryKey: ['signatures', 'quote', quoteId], queryFn: () => api<SignatureRequest[]>(`/quotes/${quoteId}/signatures`),
});
export const signatureQuery = (id: string) => queryOptions({ queryKey: ['signatures', id], queryFn: () => api<SignatureDocument>(`/signatures/${id}`) });

export const SIG_STATUS: Record<SignatureRequest['status'], [string, 'warning' | 'success' | 'neutral']> = {
  pending: ['Aguardando assinatura', 'warning'], signed: ['Assinado', 'success'], cancelled: ['Cancelado', 'neutral'],
};
export const when = (iso: string) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

/** Evidências da assinatura (o "manifesto"), no painel e na impressão. */
export function Manifest({ doc }: { doc: SignatureDocument }) {
  const e = doc.evidence;
  if (!e) return null;
  return (
    <div className="rounded-lg border border-success/30 bg-success-soft/40 p-4 text-[13px] print:border-black print:bg-transparent">
      <p className="flex items-center gap-2 font-semibold text-fg"><ShieldCheck className="size-4 text-success print:hidden" />Manifesto de assinatura eletrônica</p>
      <dl className="mt-2.5 grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
        <div><dt className="text-fg-subtle">Assinado por</dt><dd className="text-fg">{doc.signerName} (paciente {doc.patientName})</dd></div>
        <div><dt className="text-fg-subtle">Data e hora</dt><dd className="text-fg">{new Date(e.signedAt).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })} (Brasília)</dd></div>
        <div><dt className="text-fg-subtle">Meio</dt><dd className="text-fg">Área do paciente, sessão autenticada + código do app autenticador</dd></div>
        <div><dt className="text-fg-subtle">IP</dt><dd className="font-mono text-fg">{e.ip ?? '—'}</dd></div>
        <div className="sm:col-span-2"><dt className="text-fg-subtle">Aparelho</dt><dd className="break-all text-fg">{e.userAgent ?? '—'}</dd></div>
        <div className="sm:col-span-2"><dt className="text-fg-subtle">Código do documento (SHA-256)</dt><dd className="font-mono text-[12px] break-all text-fg">{e.contentSha256}</dd></div>
        <div className="sm:col-span-2"><dt className="text-fg-subtle">Declaração aceita</dt><dd className="text-fg">“{e.statement}”</dd></div>
      </dl>
    </div>
  );
}

export function SignatureViewer({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: doc } = useQuery(signatureQuery(id));
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const cancel = useMutation({
    mutationFn: () => api<void>(`/signatures/${id}/cancel`, { method: 'POST', body: { reason } }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['signatures'] }); onClose(); },
  });
  return (
    <Dialog open onClose={onClose} variant="drawer" title={doc?.title ?? 'Documento'} className="max-w-[720px]"
      description={doc ? `${SIG_STATUS[doc.status][0]} · enviado ${when(doc.createdAt)}${doc.requestedByName ? ` por ${doc.requestedByName}` : ''}` : undefined}>
      {!doc ? <Spinner className="m-5 size-5 text-fg-subtle" label="Carregando" /> : (
        <div className="grid gap-4 p-5">
          <div className="flex flex-wrap gap-2">
            <Badge tone={SIG_STATUS[doc.status][1]}>{SIG_STATUS[doc.status][0]}</Badge>
            <a href={`/documentos/${doc.id}/imprimir`} target="_blank" rel="noreferrer"><Button size="sm" variant="secondary"><Printer className="size-4" />Imprimir / PDF</Button></a>
            {doc.status === 'pending' && !confirm && <Button size="sm" variant="ghost" onClick={() => setConfirm(true)}>Cancelar pedido</Button>}
          </div>
          {confirm && (
            <form className="grid gap-2 rounded-lg border border-border p-3" onSubmit={(e) => { e.preventDefault(); cancel.mutate(); }}>
              <Field label="Motivo do cancelamento"><Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={280} autoFocus /></Field>
              {cancel.error && <Alert tone="danger">{cancel.error.message}</Alert>}
              <div className="flex gap-2"><Button size="sm" type="submit" variant="danger" disabled={reason.trim().length < 3} loading={cancel.isPending}>Cancelar pedido</Button><Button size="sm" variant="ghost" onClick={() => setConfirm(false)}>Voltar</Button></div>
            </form>
          )}
          {doc.cancelReason && <Alert tone="info">Cancelado: {doc.cancelReason}</Alert>}
          <Manifest doc={doc} />
          <div className="rounded-lg border border-border bg-surface-muted/40 p-4 text-sm leading-relaxed whitespace-pre-wrap text-fg">{doc.content}</div>
        </div>
      )}
    </Dialog>
  );
}

/** Página só do documento, para imprimir ou salvar como PDF pelo navegador. */
export function SignaturePrintPage({ id }: { id: string }) {
  const { data: doc, error } = useQuery(signatureQuery(id));
  // papel é sempre claro, mesmo com o painel no tema escuro
  useEffect(() => {
    const html = globalThis.document.documentElement;
    const prev = html.dataset.theme;
    html.dataset.theme = 'light';
    return () => { if (prev) html.dataset.theme = prev; else delete html.dataset.theme; };
  }, []);
  if (error) return <p className="p-8">{error.message}</p>;
  if (!doc) return <Spinner className="m-8 size-5" label="Carregando" />;
  return (
    <div className="mx-auto max-w-[760px] bg-white p-10 text-black print:p-0">
      <div className="mb-6 flex justify-end print:hidden"><Button onClick={() => window.print()}><Printer className="size-4" />Imprimir / salvar PDF</Button></div>
      <p className="text-sm leading-relaxed whitespace-pre-wrap">{doc.content}</p>
      <div className="mt-8"><Manifest doc={doc} /></div>
      {doc.status !== 'signed' && <p className="mt-6 text-sm font-semibold">Documento ainda não assinado ({doc.status === 'pending' ? 'aguardando o paciente' : 'cancelado'}).</p>}
    </div>
  );
}
